import {
  connectWebullPaper,
  disconnectWebullPaper,
  loadWebullPaperStatus,
  syncWebullPaper,
  type WebullPaperStatus,
} from "./ownLedgerClient";
import { notifyWebullStatusChanged } from "./webullStatus";

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function renderWebullPaperSettingsHtml(
  status: WebullPaperStatus | null,
  opts: { loading?: boolean; error?: string; variant?: "web" | "mobile" } = {},
): string {
  const variant = opts.variant ?? "web";
  const lead =
    "Paper only. Each StockForge+ user connects their own Webull sandbox, and the accounts land on that user's ledger.";
  if (opts.loading && !status) {
    return variant === "mobile"
      ? `<p class="sfe-mobile-status">Loading Webull paper…</p>`
      : `<p class="muted small sfe-column-settings-lead">Loading Webull paper…</p>`;
  }
  const connected = Boolean(status?.connected);
  const accounts = status?.accounts ?? [];
  const accountRows = accounts
    .map(
      (account) =>
        `<li>${esc(account.label)}${account.short_name ? ` <span class="muted">(${esc(account.short_name)})</span>` : ""}</li>`,
    )
    .join("");
  const hint = status?.appKeyHint ? `Key ending ${esc(status.appKeyHint)}` : "";
  const error = opts.error
    ? `<p class="muted small sfe-ledger-catalog-error" role="alert">${esc(opts.error)}</p>`
    : "";
  return `
    <p class="${variant === "mobile" ? "sfe-mobile-status" : "muted small sfe-column-settings-lead"}">${lead}</p>
    ${error}
    <p class="${variant === "mobile" ? "sfe-mobile-status" : "muted small"}">${
      connected
        ? `Connected${hint ? `. ${hint}` : ""}. Syncs ${
            (status?.refreshMinutes ?? 7) === 1
              ? "every minute"
              : `every ${status?.refreshMinutes ?? 7} minutes`
          }.`
        : "Not connected."
    }</p>
    ${
      status?.importSummary
        ? `<p class="${variant === "mobile" ? "sfe-mobile-status" : "muted small"}">Last sync wrote ${Number(status.importSummary.trades) || 0} trades and ${Number(status.importSummary.cash) || 0} cash rows.</p>`
        : ""
    }
    ${
      accountRows
        ? `<ul class="sfe-ledger-catalog-accounts">${accountRows}</ul>`
        : connected
          ? `<p class="muted small">No paper accounts on the ledger yet.</p>`
          : ""
    }
    ${
      status?.serverKeysAvailable && !connected
        ? `<p><button type="button" class="small" data-webull-paper-action="use-server">Connect server paper keys</button></p>`
        : ""
    }
    <div class="sfe-webull-paper-fields">
      <input data-webull-paper-key placeholder="Paper app key" autocomplete="off" spellcheck="false" />
      <input data-webull-paper-secret type="password" placeholder="Paper app secret" autocomplete="off" spellcheck="false" />
      <button type="button" class="small" data-webull-paper-action="connect">${connected ? "Replace keys" : "Connect"}</button>
      ${connected ? `<button type="button" class="small ghost" data-webull-paper-action="sync">Sync accounts</button>` : ""}
      ${connected ? `<button type="button" class="small ghost" data-webull-paper-action="disconnect">Disconnect</button>` : ""}
    </div>`;
}

export async function bindWebullPaperSettings(
  host: HTMLElement,
  opts: {
    variant?: "web" | "mobile";
    onError?: (message: string) => void;
    onChange?: () => void;
  } = {},
): Promise<() => void> {
  let status: WebullPaperStatus | null = null;
  const paint = (error = "") => {
    host.innerHTML = renderWebullPaperSettingsHtml(status, { error, variant: opts.variant });
  };
  const fail = (err: unknown) => {
    const message = err instanceof Error ? err.message : "Webull paper request failed";
    opts.onError?.(message);
    paint(message);
  };
  const run = async (action: "connect" | "sync" | "disconnect" | "use-server") => {
    try {
      if (action === "disconnect") {
        status = await disconnectWebullPaper();
      } else if (action === "sync" || action === "use-server") {
        status =
          action === "use-server"
            ? await connectWebullPaper({ useServerKeys: true })
            : await syncWebullPaper();
      } else {
        const appKey = host.querySelector<HTMLInputElement>("[data-webull-paper-key]")?.value.trim() ?? "";
        const appSecret = host.querySelector<HTMLInputElement>("[data-webull-paper-secret]")?.value.trim() ?? "";
        status = await connectWebullPaper({ appKey, appSecret });
      }
      paint();
      notifyWebullStatusChanged();
      opts.onChange?.();
    } catch (err) {
      fail(err);
    }
  };

  const onClick = (event: Event) => {
    const button = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>("[data-webull-paper-action]");
    if (!button || !host.contains(button)) return;
    const action = button.dataset.webullPaperAction;
    if (action === "connect" || action === "sync" || action === "disconnect" || action === "use-server") {
      button.disabled = true;
      void run(action).finally(() => {
        const next = host.querySelector<HTMLButtonElement>(`[data-webull-paper-action="${action}"]`);
        if (next) next.disabled = false;
      });
    }
  };
  host.addEventListener("click", onClick);
  try {
    status = await loadWebullPaperStatus();
    paint();
  } catch (err) {
    fail(err);
  }
  return () => host.removeEventListener("click", onClick);
}
